import { mergeFieldValues } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldValue,
  FieldValues,
  getComponentSubtree,
  setComponentSubtree,
  stripComponents,
} from 'features/libra/ontology/ontologyFieldTypes'
import { toPersistedJsonShape } from 'features/libra/ontology/persistedJson'
import omit from 'lodash/omit'

import { omitOrigin, resolveFieldOriginsForSave } from './fieldOrigin'
import type { FieldAccessScopePath } from './useScopedFieldAccess'

/** Merges one scoped save while preserving component siblings and wrapper metadata. */
export function buildFieldValuesPatch(
  latestFieldValues: FieldValues,
  scope: FieldAccessScopePath,
  values: Partial<FieldValues>
): FieldValues {
  if (scope.kind === 'ticket') {
    const existingComponents = getComponentSubtree(latestFieldValues)
    const latestTopLevel = stripComponents(latestFieldValues)
    const mergedTopLevel = omitTransientFieldState(
      mergeFieldValues(
        latestTopLevel,
        resolveFieldOriginsForSave(
          latestTopLevel,
          stripComponents(values as FieldValues)
        )
      )
    )
    return existingComponents
      ? setComponentSubtree(mergedTopLevel, existingComponents)
      : mergedTopLevel
  }

  const { componentId } = scope
  const existingComponents = getComponentSubtree(latestFieldValues) ?? {}
  const latestComponentFields = existingComponents[componentId] ?? {}
  const mergedComponentFields = omitTransientFieldState(
    mergeFieldValues(
      latestComponentFields,
      resolveFieldOriginsForSave(latestComponentFields, values as FieldValues)
    )
  )
  return setComponentSubtree(latestFieldValues, {
    ...existingComponents,
    [componentId]: mergedComponentFields,
  })
}

export function getScopedFieldValues(
  fieldValues: FieldValues,
  scope: FieldAccessScopePath
): FieldValues {
  return scope.kind === 'ticket'
    ? stripComponents(fieldValues)
    : (getComponentSubtree(fieldValues)?.[scope.componentId] ?? {})
}

/**
 * Removes state that must never be persisted: `documentsSearched` is transient
 * UI state, and a `null` `aiExtracted` is the pre-merge clear sentinel, dropped
 * so the PATCH deletes the key instead of writing a value older clients reject.
 */
function omitTransientFieldState(fieldValues: FieldValues): FieldValues {
  return Object.fromEntries(
    Object.entries(fieldValues).map(([fieldId, wrapper]) => {
      if (!wrapper) return [fieldId, wrapper]
      const persistable: FieldValue = omit(wrapper, 'documentsSearched')
      return [
        fieldId,
        persistable.aiExtracted === null
          ? omit(persistable, 'aiExtracted')
          : persistable,
      ]
    })
  )
}

/**
 * Field values as the save step would persist them, minus origin marks, so
 * dirty checks compare only real content changes against the saved ticket.
 */
export const toPersistedShape = (
  fieldValues: FieldValues
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(omitTransientFieldState(omitOrigin(fieldValues))).map(
      ([fieldId, wrapper]) => [fieldId, toPersistedJsonShape(wrapper)]
    )
  )
